import { trackContactSalesFormSubmitted } from '@gonfalon/marketo/analytics';
import { msg } from '@gonfalon/messages/msg';
import { useSubmitContactUs } from '@gonfalon/rest-api/queries/contactUs';
import { toastQueue } from '@launchpad-ui/components';

import { ContactUsDialog } from './ContactUsDialog';
import type { ContactUsFormValues, ContactUsProps } from './types';

/**
 * Contact sales, submitting through `POST /internal/contact-us` — the bespoke
 * endpoint, unconditional since its flag was removed. Kept until
 * `release-unified-form-submit` has the contact sales form ID released
 * everywhere, so an unreleased form ID still submits server-side.
 *
 * @see ContactUs
 */
export function ContactUsBackend({ plan, cta, source, ...props }: ContactUsProps) {
  const { mutate: submitToBackend, isPending } = useSubmitContactUs();

  const handleSubmit = (
    { email, company, firstName, lastName, jobTitle, moreDetails }: ContactUsFormValues,
    close: () => void,
  ) => {
    submitToBackend(
      {
        body: {
          email,
          firstName,
          lastName,
          company,
          jobTitle,
          description: moreDetails,
          referrerUrl: window.location.href,
          contactUsSource: source,
          cta,
          planType: plan?.planType,
        },
      },
      {
        onSuccess: () => {
          trackContactSalesFormSubmitted({
            planType: plan?.planType,
            component: source,
            cta,
            ...(moreDetails ? { description: moreDetails } : {}),
          });
          toastQueue.add({ title: msg('billing.contactSales.successToast'), status: 'success' });
          close();
        },
        onError: () => {
          toastQueue.add({ title: msg('billing.contactSales.errorToast'), status: 'error' });
        },
      },
    );
  };

  return (
    <ContactUsDialog plan={plan} cta={cta} source={source} {...props} isPending={isPending} onSubmit={handleSubmit} />
  );
}
